import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { PRACTICE_AREAS } from "../data/content";

const LINKS = [
  { label: "Practice", to: "/home/practice", hasDropdown: true },
  { label: "Approach", to: "/home/approach" },
  { label: "Team", to: "/home/team" },
  { label: "Firm", to: "/home/firm" },
  { label: "Insights", to: "/home/insights" },
  { label: "Book", to: "/home/book" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [practiceOpen, setPracticeOpen] = useState(false);
  const [mobilePracticeOpen, setMobilePracticeOpen] = useState(false);
  const practiceTimeout = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setPracticeOpen(false);
    setMobilePracticeOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/home" || location.pathname === "/home/";
  const isPracticeDetail =
    location.pathname.startsWith("/home/practice/") && location.pathname !== "/home/practice";
  const showSolidBg = scrolled || (!isHome && !isPracticeDetail);

  const openPractice = () => {
    if (practiceTimeout.current) clearTimeout(practiceTimeout.current);
    setPracticeOpen(true);
  };
  const closePractice = () => {
    practiceTimeout.current = setTimeout(() => setPracticeOpen(false), 120);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        showSolidBg ? "bg-[#050A14]/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Logo onClick={() => navigate("/home")} />

        <nav className="hidden lg:flex items-center gap-6">
          {LINKS.map((l) =>
            l.hasDropdown ? (
              <div
                key={l.to}
                className="relative"
                onMouseEnter={openPractice}
                onMouseLeave={closePractice}
              >
                <NavLink
                  to={l.to}
                  end
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-1 text-sm font-500 transition-colors duration-300 ${
                      isActive ? "text-[#F2EFE9]" : "text-[#94A3B8] hover:text-[#F2EFE9]"
                    }`
                  }
                >
                  {l.label}
                  <ChevronDown
                    size={14}
                    className={`transition-transform duration-300 ${practiceOpen ? "rotate-180" : ""}`}
                  />
                </NavLink>

                <AnimatePresence>
                  {practiceOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 4 }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[560px]"
                      data-testid="nav-practice-dropdown"
                    >
                      <div className="bg-[#050A14]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-3 grid grid-cols-2 gap-1">
                        {PRACTICE_AREAS.map((p) => (
                          <Link
                            key={p.slug}
                            to={`/home/practice/${p.slug}`}
                            data-testid={`nav-practice-item-${p.slug}`}
                            className="group flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-white/[0.04] transition-colors"
                          >
                            <span className="text-xs font-700 text-[#D95A41] mt-0.5 tabular-nums flex-shrink-0">
                              {p.n}
                            </span>
                            <span className="text-sm font-500 text-[#F2EFE9]/85 group-hover:text-[#F2EFE9] leading-tight">
                              {p.title}
                            </span>
                          </Link>
                        ))}
                      </div>
                      <div className="mt-1 px-3">
                        <Link
                          to="/home/practice"
                          data-testid="nav-practice-viewall"
                          className="block text-xs font-700 tracking-[0.15em] uppercase text-[#94A3B8] hover:text-[#D95A41] transition-colors py-2"
                        >
                          View all practice areas →
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-500 transition-colors duration-300 ${
                    isActive ? "text-[#F2EFE9]" : "text-[#94A3B8] hover:text-[#F2EFE9]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
          <Link
            to="/home/contact"
            data-testid="nav-engage-btn"
            className="text-sm font-700 px-5 py-2.5 rounded-full bg-[#D95A41] text-[#F2EFE9] hover:bg-[#E6735A] transition-colors duration-300"
          >
            Engage Us
          </Link>
        </nav>

        <button
          className="lg:hidden text-[#F2EFE9]"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-[#050A14]/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-3">
              {LINKS.map((l) =>
                l.hasDropdown ? (
                  <div key={l.to}>
                    <button
                      onClick={() => setMobilePracticeOpen(!mobilePracticeOpen)}
                      data-testid={`nav-mobile-${l.label.toLowerCase()}-toggle`}
                      className="w-full flex items-center justify-between text-left text-lg font-500 text-[#F2EFE9] py-1"
                    >
                      {l.label}
                      <ChevronDown
                        size={18}
                        className={`transition-transform duration-300 ${mobilePracticeOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    <AnimatePresence>
                      {mobilePracticeOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden pl-3 border-l border-white/10 ml-1 mt-2"
                        >
                          <div className="flex flex-col gap-1 py-2">
                            {PRACTICE_AREAS.map((p) => (
                              <Link
                                key={p.slug}
                                to={`/home/practice/${p.slug}`}
                                data-testid={`nav-mobile-practice-item-${p.slug}`}
                                className="flex items-start gap-3 py-2 text-sm text-[#F2EFE9]/85"
                              >
                                <span className="text-xs font-700 text-[#D95A41] tabular-nums mt-0.5">
                                  {p.n}
                                </span>
                                {p.title}
                              </Link>
                            ))}
                            <Link
                              to="/home/practice"
                              data-testid="nav-mobile-practice-viewall"
                              className="mt-1 text-xs font-700 tracking-[0.15em] uppercase text-[#94A3B8] py-2"
                            >
                              View all →
                            </Link>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end
                    data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                    className="text-left text-lg font-500 text-[#F2EFE9] py-1"
                  >
                    {l.label}
                  </NavLink>
                )
              )}
              <Link
                to="/home/contact"
                className="mt-2 text-center font-700 px-5 py-3 rounded-full bg-[#D95A41] text-[#F2EFE9]"
                data-testid="nav-mobile-engage-btn"
              >
                Engage Us
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
